import { AlertCircle, X, Loader } from 'lucide-react';

/**
 * Lightweight confirm modal used across staff workflows.
 *
 * Props:
 *  - open:    boolean controlling visibility
 *  - title:   header text
 *  - message: body text (string or node)
 *  - hint:    optional secondary instruction line (e.g. "Next step: ...")
 *  - confirmLabel: text on the destructive/affirmative button
 *  - cancelLabel:  text on the cancel button
 *  - accent:  'primary' | 'success' | 'danger'  (default 'primary')
 *  - busy:    boolean — disables buttons while async work is in flight
 *  - onConfirm / onClose
 */
const ACCENT = {
  primary: { btn: 'bg-[#0066FF] hover:bg-[#0052cc]',  iconBg: 'bg-blue-100',  iconFg: 'text-[#0066FF]' },
  success: { btn: 'bg-[#10B981] hover:bg-[#059669]',  iconBg: 'bg-green-100', iconFg: 'text-[#10B981]' },
  danger:  { btn: 'bg-red-600  hover:bg-red-700',     iconBg: 'bg-red-100',   iconFg: 'text-red-600'    },
};

const ConfirmDialog = ({
  open,
  title,
  message,
  hint,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  accent = 'primary',
  busy = false,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;
  const a = ACCENT[accent] || ACCENT.primary;

  return (
    <div
      data-testid="confirm-dialog"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-xl border border-gray-200 p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {!busy && (
          <button
            data-testid="confirm-dialog-close"
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        )}

        <div className="flex items-start gap-3 mb-4">
          <div className={`w-11 h-11 rounded-full ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
            <AlertCircle size={20} className={a.iconFg} />
          </div>
          <div>
            <h2
              className="text-xl text-gray-900"
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
            >
              {title}
            </h2>
            {message && (
              <div className="text-sm text-gray-600 mt-1 leading-relaxed">{message}</div>
            )}
          </div>
        </div>

        {hint && (
          <div
            data-testid="confirm-dialog-hint"
            className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4"
          >
            <strong className="font-semibold">Next step:</strong> {hint}
          </div>
        )}

        <div className="flex gap-3">
          <button
            data-testid="confirm-dialog-cancel"
            onClick={onClose}
            disabled={busy}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl h-12 transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            data-testid="confirm-dialog-confirm"
            onClick={onConfirm}
            disabled={busy}
            className={`flex-1 ${a.btn} disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold rounded-xl h-12 transition-colors flex items-center justify-center gap-2`}
          >
            {busy ? <><Loader className="animate-spin" size={16} /> Working…</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
